import { DateTime } from "luxon";
import { ref, computed, onUnmounted, type Ref } from "vue";

export interface TimerDisplay {
  hours: string;
  minutes: string;
  seconds: string;
  totalSeconds: number;
  isExpired: boolean;
}

export interface TimerStartOptions {
  expiresAt: DateTime;
  onExpire?: () => void;
}

export const useTimer = () => {
  // State
  const isRunning: Ref<boolean> = ref(false);
  const currentTime: Ref<DateTime> = ref(DateTime.now());
  const expiresAtRef: Ref<DateTime | null> = ref(null);
  const hasExpired: Ref<boolean> = ref(false);

  let intervalId: number | null = null;
  let onExpireCallback: (() => void) | null = null;

  // Computed timer display with memoization
  const display = computed<TimerDisplay>(() => {
    const now = currentTime.value;
    const end = expiresAtRef.value;

    // Return empty state if timer hasn't started
    if (!end) {
      return {
        hours: "00",
        minutes: "00",
        seconds: "00",
        totalSeconds: 0,
        isExpired: false,
      };
    }

    // Calculate difference in seconds
    const diff = end.diff(now, "seconds").seconds;
    const totalSeconds = Math.max(0, Math.floor(diff));

    // Check if expired
    const isExpired = totalSeconds <= 0;

    if (isExpired && !hasExpired.value) {
      hasExpired.value = true;
      stop();
      // Run callback in next tick to avoid blocking
      if (onExpireCallback) {
        setTimeout(() => onExpireCallback!(), 0);
      }
    }

    // Calculate hours, minutes, seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      hours: String(hours).padStart(2, "0"),
      minutes: String(minutes).padStart(2, "0"),
      seconds: String(seconds).padStart(2, "0"),
      totalSeconds,
      isExpired,
    };
  });

  // Start the countdown timer
  function start(options: TimerStartOptions): void {
    const { expiresAt, onExpire } = options;

    // Stop any existing timer
    if (isRunning.value) {
      stop();
    }

    // Set new options
    expiresAtRef.value = expiresAt;
    onExpireCallback = onExpire ?? null;
    hasExpired.value = false;
    isRunning.value = true;
    currentTime.value = DateTime.now();

    // Start ticking
    const tick = () => {
      currentTime.value = DateTime.now();

      if (isRunning.value && !hasExpired.value) {
        intervalId = window.setTimeout(tick, 1000) as unknown as number;
      }
    };

    tick();
  }

  // Stop the countdown timer
  function stop(): void {
    isRunning.value = false;
    if (intervalId !== null) {
      clearTimeout(intervalId);
      intervalId = null;
    }
  }

  // Reset the timer (clears all state)
  function reset(): void {
    stop();
    hasExpired.value = false;
    expiresAtRef.value = null;
    onExpireCallback = null;
    currentTime.value = DateTime.now();
  }

  // Pause the timer (keeps current time)
  function pause(): void {
    stop();
  }

  // Resume the timer
  function resume(): void {
    if (!hasExpired.value && expiresAtRef.value) {
      isRunning.value = true;

      const tick = () => {
        currentTime.value = DateTime.now();

        if (isRunning.value && !hasExpired.value) {
          intervalId = window.setTimeout(tick, 1000) as unknown as number;
        }
      };

      tick();
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stop();
  });

  return {
    display,
    isRunning,
    hasExpired,
    start,
    stop,
    reset,
    pause,
    resume,
  };
};
